import { api } from './api';

export interface CreateDealData {
  storeId?: string;
  itemName: string;
  description: string;
  category: string;
  photoUrl?: string;
  originalPrice: number;
  currency: string;
  sizes: string[];
  colors: string[];
  slotCount: number;
  decayCurve: {
    tiers: { fromMinutes: number; toMinutes: number; discountPercent: number }[];
    expiresAfterMinutes: number;
  };
  publishNow: boolean;
  scheduledFor?: string;
}

export interface Deal {
  id: string;
  storeId: string;
  item: {
    name: string;
    description: string;
    category: string;
    photoUrl?: string;
    originalPrice: number;
    currency: string;
    sizes: string[];
    colors: string[];
  };
  decayCurve: {
    tiers: { fromMinutes: number; toMinutes: number; discountPercent: number }[];
    expiresAfterMinutes: number;
  };
  slotsTotal: number;
  slotsClaimed: number;
  status: string;
  publishedAt?: string;
  scheduledFor?: string;
  expiresAt?: string;
  createdAt: string;
  metrics: {
    notificationsSent: number;
    rushesStarted: number;
    rushesCompleted: number;
    claims: number;
    avgArrivalSeconds: number;
    revenueGenerated: number;
  };
}

export interface DealAnalytics {
  dealId: string;
  claimsOverTime: { date: string; count: number }[];
  claimsByStatus: { status: string; count: number }[];
  totalClaims: number;
  conversionRate: number;
  totalRevenue: number;
}

export interface StoreAnalytics {
  totalDeals: number;
  totalClaims: number;
  totalRevenue: number;
  avgClaimRate: number;
  claimsOverTime: { date: string; count: number }[];
}

export const dealService = {
  createDeal: async (data: CreateDealData): Promise<Deal> => {
    // Resolve storeId if not provided
    let storeId = data.storeId;
    if (!storeId) {
      const { data: stores } = await api.get<{ id: string }[]>('/merchants/me/stores');
      if (!stores.length) throw new Error('No stores found. Create a store first.');
      storeId = stores[0].id;
    }

    // Transform flat form data to nested API DTO format
    const payload = {
      storeId,
      item: {
        name: data.itemName,
        description: data.description,
        category: data.category,
        images: data.photoUrl ? [data.photoUrl] : [],
        sizes: data.sizes,
        colors: data.colors,
      },
      originalPrice: data.originalPrice,
      currency: data.currency,
      decay: {
        curve: data.decayCurve.tiers,
        expiresAfterMinutes: data.decayCurve.expiresAfterMinutes,
      },
      slotsTotal: data.slotCount,
      scheduledFor: data.publishNow ? undefined : data.scheduledFor,
    };

    const { data: response } = await api.post<Deal>('/merchants/deals', payload);
    return response;
  },

  getActiveDeals: async (): Promise<Deal[]> => {
    const { data } = await api.get<Deal[]>('/merchants/deals?status=active,paused');
    return data;
  },

  getDealHistory: async (): Promise<Deal[]> => {
    const { data } = await api.get<Deal[]>('/merchants/deals?status=completed,expired,cancelled');
    return data;
  },

  getScheduledDeals: async (): Promise<Deal[]> => {
    const { data } = await api.get<Deal[]>('/merchants/deals?status=scheduled');
    return data;
  },

  pauseDeal: async (id: string): Promise<Deal> => {
    const { data } = await api.put<Deal>(`/merchants/deals/${id}/pause`);
    return data;
  },

  resumeDeal: async (id: string): Promise<Deal> => {
    const { data } = await api.put<Deal>(`/merchants/deals/${id}/resume`);
    return data;
  },

  cancelDeal: async (id: string): Promise<Deal> => {
    const { data } = await api.put<Deal>(`/merchants/deals/${id}/cancel`);
    return data;
  },

  duplicateDeal: async (id: string): Promise<Deal> => {
    const { data } = await api.post<Deal>(`/merchants/deals/${id}/duplicate`);
    return data;
  },

  getDealAnalytics: async (id: string): Promise<DealAnalytics> => {
    const { data } = await api.get<DealAnalytics>(`/merchants/deals/${id}/analytics`);
    return data;
  },

  generateScannerPin: async (): Promise<{ pin: string }> => {
    const { data } = await api.post<{ pin: string }>('/merchants/scanner/pin');
    return data;
  },

  getDashboardStats: async (): Promise<{
    activeDeals: number;
    todayClaims: number;
    todayRevenue: number;
    avgArrivalTime: number;
    recentActivity: {
      id: string;
      type: string;
      dealName: string;
      customerName: string;
      discount: number;
      timestamp: string;
    }[];
  }> => {
    const { data } = await api.get('/merchants/dashboard/stats');
    return data;
  },

  getStoreAnalytics: async (from?: string, to?: string): Promise<StoreAnalytics> => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const { data } = await api.get<StoreAnalytics>(`/merchants/analytics?${params.toString()}`);
    return data;
  },

  uploadPhoto: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post<{ url: string }>('/merchants/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};
